import Link from 'next/link'
import React from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Box, Container, styled, Typography } from '@mui/material'

type FooterLink = {
  name: string
  href: string
  target?: '_self' | '_blank'
}

export type FooterInfoBarProps = {
  abn?: string
  copyright?: string
  terms?: FooterLink
  privacy?: FooterLink
  marketSelector?: React.ReactNode
  onCookiePreferences?: () => void
}

export const FooterInfoBar: React.FC<FooterInfoBarProps> = ({
  abn,
  copyright,
  terms,
  privacy,
  marketSelector,
  onCookiePreferences,
}) => (
  <InfoBarContainer>
    <Container maxWidth="lg">
      <InfoBarRow>
        <InfoBarSection>
          <MarketSelectorWrapper>
            {marketSelector}
          </MarketSelectorWrapper>
        </InfoBarSection>

        <InfoBarSection sx={{ textAlign: 'center' }}>
          <InfoBarTypography>{copyright}</InfoBarTypography>
          {abn && <InfoBarTypography>{abn}</InfoBarTypography>}
        </InfoBarSection>

        <InfoBarSection sx={{ justifyContent: 'flex-end' }}>
          <InfoBarLinksGroup>
            {terms && (
              <Link href={terms.href} target={terms.target || '_self'}>
                <InfoBarLinkTypography>{terms.name}</InfoBarLinkTypography>
              </Link>
            )}
            {privacy && (
              <Link href={privacy.href} target={privacy.target || '_self'}>
                <InfoBarLinkTypography>{privacy.name}</InfoBarLinkTypography>
              </Link>
            )}
            {onCookiePreferences && (
              <InfoBarLinkTypography
                as="button"
                onClick={onCookiePreferences}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Cookie Preferences
              </InfoBarLinkTypography>
            )}
          </InfoBarLinksGroup>
        </InfoBarSection>
      </InfoBarRow>
    </Container>
  </InfoBarContainer>
)

const InfoBarContainer = styled('div')(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  paddingTop: pxToRem(16),
  paddingBottom: pxToRem(16),
  color: theme.palette.custom.footer.text,
}))

const InfoBarRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(12),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 0,
  },
}))

const InfoBarSection = styled(Box)(({ theme }) => ({
  display: 'flex',

  [theme.breakpoints.up('sm')]: {
    flex: 1,
  },
}))

const MarketSelectorWrapper = styled(Box)(() => ({}))

const InfoBarTypography = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(12),
  color: theme.palette.custom.footer.text,
}))

const InfoBarLinkTypography = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: pxToRem(12),
  fontWeight: 300,
  color: theme.palette.custom.footer.text,
  textDecorationColor: theme.palette.custom.footer.text,
}))

const InfoBarLinksGroup = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(24),
}))
